import React, { useEffect, useMemo } from "react";
import moment, { type Moment, type unitOfTime } from "moment";
import { type DropdownItemProps, type DropdownProps } from "semantic-ui-react";
import DatePicker, { type PopupPosition } from "../datePicker/DatePicker";
import { type LicenseType } from "./types/LicenseType";
import LicenseDurationType from "../../enums/licensing/licenseDurationType";
import { LicenseTypes } from "../../enums/licensing/licenseType";
import cn from "classnames";
import { DropdownWrapper } from "components/dropdowns";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import styles from "./LicensePicker.module.scss";
import FieldLabel from "components/forms/FieldLabel";

export interface LicensePickerProps {
  options: LicenseType[];
  preselectedOptionId?: number;
  preselectedExpirationDate?: Date;
  onOptionChange?: (optionId?: number) => void;
  onExpirationDateChange?: (endDate?: Date) => void;
  verticalPosition?: "top" | "bottom";
  popupPosition?: PopupPosition;
  highlightExpired?: boolean;
  highlightExpiredTrial?: boolean;
  noExpiration?: boolean;
  className?: string;
  labelClassName?: string;
}

const LicensePicker: React.FC<LicensePickerProps> = ({
  verticalPosition = "bottom",
  className,
  labelClassName,
  ...props
}) => {
  const defaultLicenseType = useMemo(
    () => (props.noExpiration ? props.options.find((o) => o.id === LicenseTypes.CustomDate) : null),
    [props.noExpiration, props.options],
  );

  const [selectedOption, setSelectedOption] = React.useState<null | LicenseType>(
    props.noExpiration && !!defaultLicenseType ? defaultLicenseType : null,
  );

  const [expirationDate, setExpirationDate] = React.useState<null | Moment>(
    props.preselectedExpirationDate ? moment(props.preselectedExpirationDate) : null,
  );

  const paidPacks = useFeatureFlag(FeatureFlags.PaidPacks);

  const defaultDurationType: unitOfTime.DurationConstructor = "M";
  const minDate: Moment = moment().add(1, defaultDurationType);
  const maxDate: Moment = moment().add(5, "y"); // maxDate is 5 years due to req

  const dropdownOptions: DropdownItemProps[] = props.options.map((option) => {
    return {
      text: option.title,
      value: option.id,
    };
  });

  const { onOptionChange, onExpirationDateChange, highlightExpired, highlightExpiredTrial } = props;

  useEffect(() => {
    const currentSelectedOption = props.preselectedOptionId
      ? props.options.find((o) => o.id === props.preselectedOptionId)
      : null;
    setSelectedOption(currentSelectedOption || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.preselectedOptionId]);

  useEffect(() => {
    onOptionChange && onOptionChange(selectedOption?.id || props.preselectedOptionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setExpirationDate(getDate(selectedOption));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  useEffect(() => {
    onExpirationDateChange && onExpirationDateChange(expirationDate?.toDate());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expirationDate]);

  const getDate = (option?: null | LicenseType): null | Moment => {
    // custom date selection
    if (!option?.duration) {
      return props.preselectedExpirationDate ? moment(props.preselectedExpirationDate) : null;
    }

    return moment().add(option?.duration, getDurationType(option?.durationType));
  };

  const getDurationType = (durationType?: LicenseDurationType): unitOfTime.DurationConstructor => {
    switch (durationType) {
      case LicenseDurationType.Days:
        return "d";
      case LicenseDurationType.Months:
        return "M";
      default:
        return defaultDurationType;
    }
  };

  const handleOptionChange = (e: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    let selected = props.options.find((item) => item.id === data.value);
    if (selected) {
      setSelectedOption(selected);
      onOptionChange && onOptionChange(selected.id);
    }
  };

  const handleDateChange = (_: React.SyntheticEvent<HTMLElement>, data: any) => {
    let newDate = moment(new Date(`${data.value} ${moment().local().format("HH:mm:ss")}`));
    const isDateValid = newDate.isAfter(minDate, "day") && newDate.isSameOrBefore(maxDate, "day");
    setExpirationDate(isDateValid ? newDate : minDate);
  };

  const shouldRenderExpirationDate = (option: LicenseType | null) =>
    option && option.id !== LicenseTypes.Trial && option.duration === undefined;

  const renderExpirationDate = () => {
    return (
      <div className={styles.expirationDate}>
        <FieldLabel label={"Date"} className={labelClassName} />
        <DatePicker
          value={expirationDate ? expirationDate.format("MM/DD/YYYY") : ""}
          onChangeDate={handleDateChange}
          disabled={!selectedOption || selectedOption.duration !== undefined}
          minDate={minDate}
          initialDate={minDate}
          marked={expirationDate ? [] : [minDate]}
          maxDate={maxDate}
          placeholder="Select"
          verticalPosition={verticalPosition}
          popupPosition={props.popupPosition}
          highlightExpired={!!(highlightExpired && expirationDate && moment(expirationDate).isBefore(moment(), "day"))}
          className={styles.datePicker}
        />
      </div>
    );
  };

  return (
    <div className={styles.row}>
      <div className={cn(styles.type, className)}>
        <FieldLabel
          label={"Expiration"}
          markAsRequired={!paidPacks || !props.noExpiration}
          className={labelClassName}
        />
        {paidPacks && props.noExpiration ? (
          <span>
            <b>Never</b>
          </span>
        ) : (
          <>
            <DropdownWrapper
              blur
              selection
              value={selectedOption?.id}
              handleOptionChange={handleOptionChange}
              items={dropdownOptions}
              className={cn("field", styles.datePicker, { expired: highlightExpiredTrial })}
              multiple={false}
              clearable={false}
              data-testid="license-picker"
              placeholder="Select Expiration"
            />
          </>
        )}
      </div>
      {!(paidPacks && props.noExpiration) && shouldRenderExpirationDate(selectedOption) && renderExpirationDate()}
    </div>
  );
};

export default LicensePicker;
